import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { linkStyle } from "@repo/ui/Link/index";
import { useState, type FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { FieldDiffView } from "./recordingAssessmentEditor/fieldDiffView.js";

export type FieldView = "edit" | "diff";

export interface FieldDiffState {
  /** The field's value differs from what it is diffed against. */
  hasChanges: boolean;
  /** `diff` only while the toggle is on and there is something to compare. */
  view: FieldView;
  onToggle: () => void;
}

/**
 * Tracks whether a field is showing its editor or a diff against the value it
 * was changed from. The toggle resets once the two match — after a save, a
 * cancel, or a discarded suggestion — so the next edit starts in the editor
 * rather than in a diff nobody asked for.
 *
 * Use it directly where the field's label row belongs to something else (the
 * automation form's `ConfigurationField` hosts the toggle itself); everywhere
 * else {@link DiffableField} renders the row.
 */
export function useFieldDiffState(
  savedValue: string | undefined,
  value: string,
): FieldDiffState {
  const [view, setView] = useState<FieldView>("edit");
  const hasChanges = savedValue !== undefined && savedValue !== value;
  if (!hasChanges && view === "diff") {
    setView("edit");
  }
  return {
    hasChanges,
    view: hasChanges ? view : "edit",
    onToggle: () => {
      setView((current) => (current === "edit" ? "diff" : "edit"));
    },
  };
}

/** Switches a field between its editor and a diff against the saved value. */
export const FieldDiffToggle: FC<{
  view: FieldView;
  onToggle: () => void;
}> = ({ view, onToggle }) => (
  <Button
    variant={ButtonVariant.Tertiary}
    size={Size.MEDIUM}
    className={twMerge("cursor-pointer pr-0", linkStyle())}
    onClick={onToggle}
  >
    {view === "edit" ? "See changes" : "Edit"}
  </Button>
);

/**
 * A labeled field that can show what changed: its editor by default, and a
 * word diff against `savedValue` while "See changes" is on. The toggle only
 * appears once the two differ, so an untouched field carries no extra chrome.
 *
 * The editor is the caller's — an input, a textarea, anything text-shaped —
 * so the diff view fits playbook, signal, and automation forms alike.
 */
export const DiffableField: FC<{
  label: ReactNode;
  /** What the field is diffed against. Absent where there is nothing to compare to. */
  savedValue: string | undefined;
  value: string;
  /** Sizing for the diff view, matched to the editor it replaces. */
  diffClassName?: string;
  children: ReactNode;
}> = ({ label, savedValue, value, diffClassName, children }) => {
  const diff = useFieldDiffState(savedValue, value);

  return (
    <>
      <div className="mb-1 flex min-h-8 items-center justify-between">
        {label}
        {diff.hasChanges ? (
          <FieldDiffToggle view={diff.view} onToggle={diff.onToggle} />
        ) : null}
      </div>
      {diff.view === "diff" && savedValue !== undefined ? (
        <FieldDiffView
          className={diffClassName}
          savedValue={savedValue}
          editedValue={value}
        />
      ) : (
        children
      )}
    </>
  );
};
